import type { Asset, FormSchema, IssuedKitItem } from '@hcc/types';
import { STATIC_FIELD_TYPES } from '@hcc/types';
import type { FormValues } from '../../onboarding/formfields/formValues';

export interface PrefillContext {
  personName: string;
  items: IssuedKitItem[];
  /** Full asset rows for tracked items, keyed by asset_id, for metadata. */
  assetsById: Record<string, Asset | undefined>;
  /** Today's date as YYYY-MM-DD (the drawer passes it in). */
  today: string;
}

/**
 * Seed obvious answers on a desk handover form from what the drawer already
 * knows: the vehicle's registration / make / model from the tracked asset,
 * the driver's name, and today's date. Matching is by field key convention
 * (the builder shows keys), so authors opt in by naming fields accordingly;
 * anything unmatched simply starts blank. Everything stays editable.
 */
export function buildDeskPrefill(schema: FormSchema, ctx: PrefillContext): FormValues {
  const vehicleItem = ctx.items.find((i) => i.registration || i.asset_id);
  const asset = vehicleItem?.asset_id ? ctx.assetsById[vehicleItem.asset_id] : undefined;
  const meta = (asset?.metadata ?? {}) as Record<string, unknown>;
  const registration =
    vehicleItem?.registration ?? (typeof meta.registration === 'string' ? meta.registration : undefined);
  const make = typeof meta.make === 'string' ? meta.make : undefined;
  const model = typeof meta.model === 'string' ? meta.model : undefined;

  const nameParts = ctx.personName.trim().split(/\s+/);
  const first = nameParts[0] ?? '';
  const last = nameParts.slice(1).join(' ');

  const values: FormValues = {};
  for (const page of schema.pages) {
    for (const f of page.fields) {
      if (STATIC_FIELD_TYPES.includes(f.type)) continue;
      const key = f.key.toLowerCase();
      if (
        registration &&
        (f.type === 'short_text' || f.type === 'long_text') &&
        /registration|number_plate|license_plate|licence_plate|vrn/.test(key)
      ) {
        values[f.key] = registration;
      } else if (make && f.type === 'short_text' && /(^|_)make($|_)/.test(key)) {
        values[f.key] = make;
      } else if (model && f.type === 'short_text' && /(^|_)model($|_)/.test(key)) {
        values[f.key] = model;
      } else if (f.type === 'name' && /driver|engineer/.test(key)) {
        values[f.key] = [first, last];
      } else if (f.type === 'date' && /(^|_)date($|_)/.test(key)) {
        values[f.key] = ctx.today;
      }
    }
  }
  return values;
}
